import { useState } from "react";
import { motion } from "framer-motion";
import { Gauge, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { VEHICLE_IMAGES } from "@/lib/images";

const TABS = [
  { id: "all", label: "All Fleet" },
  { id: "truck", label: "Trucks & SUVs" },
  { id: "exotic", label: "Exotics" },
];

export const FleetSection = ({ fleet = [], onReserve }) => {
  const [tab, setTab] = useState("all");
  const visible = tab === "all" ? fleet : fleet.filter((v) => v.category === tab);

  return (
    <section id="fleet" data-testid="fleet-section" className="bg-carbon/40 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <Reveal>
          <p className="eyebrow">The Fleet · Chauffeur or Self-Drive</p>
          <h2 className="mt-4 font-serif text-3xl font-semibold text-slate-100 sm:text-4xl lg:text-5xl">
            Blacked-Out. <span className="gold-gradient-text italic">Detailed. Waiting.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="fleet-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`fleet-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative rounded-full px-6 py-2.5 font-display text-xs tracking-[0.2em] uppercase transition-colors ${
                  tab === t.id ? "text-obsidian" : "text-zinc-400 hover:text-gold"
                }`}
              >
                {tab === t.id && (
                  <motion.span
                    layoutId="fleet-tab-pill"
                    className="absolute inset-0 rounded-full bg-gold"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <span className="relative z-10">{t.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((v, i) => (
            <Reveal key={v.id} delay={0.06 * i}>
              <div
                data-testid={`vehicle-card-${v.id}`}
                className="spotlight-card group flex h-full flex-col transition-colors duration-300 hover:border-gold/40"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={VEHICLE_IMAGES[i % VEHICLE_IMAGES.length]}
                    alt={`${v.name} interior`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-carbon via-transparent to-transparent" />
                  <span className="absolute top-4 right-4 rounded-full border border-gold/30 bg-black/60 px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-gold uppercase backdrop-blur-sm">
                    {v.category === "truck" ? "Truck / SUV" : "Exotic"}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-gold uppercase">{v.model}</p>
                  <h3 className="mt-2 font-serif text-2xl text-slate-100">{v.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-500">{v.blurb}</p>
                  <div className="mt-5 flex items-center gap-6 font-mono text-[11px] tracking-wider text-zinc-400">
                    <span className="flex items-center gap-2">
                      <Gauge size={13} className="text-gold" /> {v.horsepower} HP
                    </span>
                    <span className="flex items-center gap-2">
                      <Users size={13} className="text-gold" /> {v.seats} Seats
                    </span>
                  </div>
                  <div className="mt-6 flex items-center justify-between border-t border-gold/10 pt-5">
                    <p className="font-serif text-xl text-slate-100">
                      ${v.daily_rate}
                      <span className="ml-1 font-sans text-xs text-zinc-500">/ day</span>
                    </p>
                    <button
                      data-testid={`vehicle-reserve-btn-${v.id}`}
                      onClick={() => onReserve("vehicle", v.id)}
                      className="rounded-full border border-gold/40 px-5 py-2 font-display text-[11px] font-semibold tracking-[0.2em] text-gold uppercase transition-all hover:bg-gold hover:text-obsidian"
                    >
                      Reserve
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
